/**
 * School ID Generation Utilities
 * 
 * Provides functions for generating and validating unique 8-character
 * alphanumeric School IDs for multi-tenancy support.
 */

const SCHOOL_ID_LENGTH = 8;
const SCHOOL_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const SCHOOL_ID_REGEX = /^[A-Z0-9]{8}$/;

/**
 * Generate a random 8-character alphanumeric School ID
 * Format: [A-Z0-9]{8}
 * Example: "A3B7K9M2"
 * 
 * @returns {string} 8-character School ID
 */
export function generateSchoolId(): string {
  let schoolId = '';
  
  for (let i = 0; i < SCHOOL_ID_LENGTH; i++) {
    const randomIndex = Math.floor(Math.random() * SCHOOL_ID_CHARS.length);
    schoolId += SCHOOL_ID_CHARS[randomIndex];
  }
  
  return schoolId;
}

/**
 * Generate a unique School ID with collision check
 * Attempts to generate a unique ID up to maxAttempts times
 * 
 * @param {Function} checkExists - Async function that checks if a School ID exists
 * @param {number} maxAttempts - Maximum number of generation attempts (default: 10)
 * @returns {Promise<string>} Unique 8-character School ID
 * @throws {Error} If unable to generate unique ID after maxAttempts
 */
export async function generateUniqueSchoolId(
  checkExists: (schoolId: string) => Promise<boolean>,
  maxAttempts: number = 10
): Promise<string> {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    const schoolId = generateSchoolId();
    const exists = await checkExists(schoolId);
    
    if (!exists) {
      return schoolId;
    }
    
    attempts++;
  }
  
  throw new Error('Failed to generate unique School ID after maximum attempts');
}

/**
 * Validate School ID format
 * Checks if the provided string matches the required format: exactly 8 alphanumeric characters
 * 
 * @param {string} schoolId - School ID to validate
 * @returns {boolean} True if format is valid, false otherwise
 */
export function validateSchoolIdFormat(schoolId: string): boolean {
  if (!schoolId || typeof schoolId !== 'string') {
    return false;
  }
  
  return SCHOOL_ID_REGEX.test(schoolId);
}
